<script context="module" lang="ts">
</script>

<script lang="ts">
  import {onMount} from 'svelte';
  import {getDrawerStore} from '@skeletonlabs/skeleton';
  import {FlagBanner} from 'phosphor-svelte';
  import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import {showToast} from '$lib/helpers/toastrHelper';
  import {
    getLanguageName,
    getLocationName,
    getKeyDescriptions,
    localisationStore,
    searchLocalisations,
    upsertForCulture,
    type LocalisationItem, upsertForClient, type LocaleKey
  } from "$lib/stores/localisationStore";

  const drawerStore = getDrawerStore();
  let loading: boolean = true;
  let saving: boolean = false;
  let descriptions: Record<string, string> = {};

  $: selectedClientId = $localisationStore.selectedClientId;
  $: selectedLocale = $localisationStore.selectedLocale;
  $: entries = $localisationStore.localisationSearchResults;

  onMount(async () => {
    try {
      const [, descs] = await Promise.all([
        searchLocalisations({
          clientId: selectedClientId,
          location: selectedLocale?.location,
          language: selectedLocale?.language ?? 0
        }),
        getKeyDescriptions().catch(() => ({} as Record<string, string>)),
      ]);
      descriptions = descs;
    } finally {
      loading = false;
    }
  });

  function onCloseClick() {
    drawerStore.close();
  }

  function getChangedEntries(): LocalisationItem[] {
    const original = new Map($localisationStore.localisationOriginalSearchResults.map(e => [e.key, e.value]));
    return $localisationStore.localisationSearchResults.filter(e => original.get(e.key) !== e.value);
  }

  function getTitle(selectedClientId?: number, selectedLocale?: LocaleKey): string {
    if (selectedClientId) {
      return `Edit Localisations (Client ${selectedClientId})`;
    } else if (selectedLocale) {
      return `Edit Localisations (${getLanguageName(selectedLocale.language)} - ${getLocationName(selectedLocale.location)})`;
    }

    return '';
  }

  async function onSave(): Promise<void> {
    const changed = getChangedEntries();
    console.log('changed', changed);
    if (!selectedClientId && !selectedLocale || changed.length === 0) {
      showToast({title: 'Done', message: 'No changes to save.', type: 'info'});
      drawerStore.close();
      return;
    }
    saving = true;
    try {
      if (selectedClientId) {
        await upsertForClient({
          clientId: selectedClientId,
          entries: changed,
        });
      } else if (selectedLocale) {
        await upsertForCulture({
          location: selectedLocale.location,
          language: selectedLocale.language,
          entries: changed,
        });
      }
      showToast({
        title: 'Saved',
        message: `Updated ${changed.length} localisation${changed.length === 1 ? '' : 's'}.`,
        type: 'success'
      });
      drawerStore.close();
    } catch {
      showToast({title: 'Error', message: 'Failed to save localisation.', type: 'error'});
      // store already toasts the error
    } finally {
      saving = false;
    }
  }
</script>

<div class="card flex h-full flex-col">
  <header class="flex justify-between border-b p-5">
    <div class="inline-flex items-center gap-2 p-4 text-2xl font-bold">
      <span>
        <FlagBanner/>
      </span>
      <span>{getTitle(selectedClientId, selectedLocale)}</span>
    </div>
    <div class="content-center">
      <IconButton icon="x" onClick={onCloseClick}/>
    </div>
  </header>
  <div class="flex-grow overflow-y-auto p-4">
    {#if loading}
      <p>Loading...</p>
    {:else if entries.length === 0}
      <p class="p-5 text-surface-500">No localisation entries for this locale.</p>
    {:else}
      <div class="flex flex-col gap-4 p-5">
        {#each entries as entry (entry.key)}
          <label class="label">
            <span class="text-sm font-medium">{entry.key}</span>
            <input class="input w-full" type="text" bind:value={entry.value}/>
            {#if descriptions[entry.key]}
              <span class="text-xs text-surface-900">{descriptions[entry.key]}</span>
            {/if}
          </label>
        {/each}
      </div>
    {/if}
  </div>
  <footer class="flex w-full justify-between gap-5 border-t p-4">
    <Button label="Cancel" size="large" variant="outlined" onClick={onCloseClick}/>
    <div class="inline-flex flex-wrap gap-5">
      <Button label="Save Changes" colour="primary" size="large" disabled={saving || loading} onClick={onSave}/>
    </div>
  </footer>
</div>
